import {FC, useEffect, useState} from 'react';

import {Wrapper, Status} from '@googlemaps/react-wrapper';
import {Box, DialogContent, DialogTitle, IconButton, Typography, useMediaQuery} from '@mui/material';
import {useTranslation} from 'react-i18next';
import usePlacesAutocomplete, {getDetails} from 'use-places-autocomplete';

import ChevronLeftIcon from 'assets/icons/chevronLeft';
import {Loading} from 'components/loader/Loading';
import {config} from 'config/env';
import theme from 'config/theme';
import {ANALYTICS} from 'contexts/analytics-context';
import {useAppSelector} from 'hooks';
import {TPinMapSelectedLocation} from 'types';
import {extractGoogleAddressComponents} from 'utils/googleMapHelper';
import {BangkokCoordinates} from 'views/map-wrapper/MapWrapper';

import {AddressSearchListView} from './AddressSearchListView';
import {PinMapView} from './PinMapView';
import SearchAddressInput from './SearchAddressInput';
import {StyledAddressFormDialog} from './styles';

interface Props {
  open: boolean;
  onSave: (newLocationData: TPinMapSelectedLocation) => void;
  initialPinMapData?: TPinMapSelectedLocation;
  isAddressFormOpen: boolean;
  addAddressEvent: (eventName: string, payload: any) => void;
  loading?: boolean;
  setHasUserMovedMapManually: (value: boolean) => void;
}

export const PinMapModal: FC<Props> = ({
  open,
  onSave,
  initialPinMapData,
  isAddressFormOpen,
  addAddressEvent,
  loading,
  setHasUserMovedMapManually
}) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
  const [selectedLocation, setSelectedLocation] = useState<TPinMapSelectedLocation>(initialPinMapData || null);
  const [inputValue, setInputValue] = useState('');
  const initialDefaultCoordinates = selectedLocation?.coordinates || BangkokCoordinates;
  const {currentStore} = useAppSelector((state) => state?.app);
  const [isMapsApiLoaded, setIsMapsApiLoaded] = useState(false);

  const {
    ready,
    init,
    suggestions,
    setValue: setSearchValue,
    value: searchValue,
    clearSuggestions
  } = usePlacesAutocomplete({
    debounce: 1000,
    initOnMount: false,
    requestOptions: {
      componentRestrictions: {country: 'th'}
    }
  });

  useEffect(() => {
    if (isMapsApiLoaded) {
      init(); //initialize autocomplete api
    }
  }, [isMapsApiLoaded, init]);

  useEffect(() => {
    if (ready && isMapsApiLoaded && suggestions.status === 'OK' && suggestions.data?.length > 0) {
      addAddressEvent(ANALYTICS.ADDRESS_DECODED, {type: 'geocoding', googleResponse: suggestions});
    }
  }, [addAddressEvent, isMapsApiLoaded, ready, suggestions]);

  // Handle input change and prevent API calls for less than 3 characters
  const handleInputChange = (iv: string) => {
    setInputValue(iv);
    const trimmedInput = iv.trim();

    if (searchValue === trimmedInput) return;

    if (trimmedInput.length >= 3) {
      setSearchValue(trimmedInput);
    } else {
      setSearchValue(''); // Clear value if less than 3 chars to avoid API calls
      clearSuggestions();
    }
  };

  // on places suggestions result select
  const onSuggestionSelect = async (place_id: string, description: string) => {
    try {
      //pulling more details from place id, bcoz we don't have formatted address and coordinates
      const placeDetails = (await getDetails({
        placeId: place_id,
        fields: ['address_components', 'geometry']
      })) as google.maps.places.PlaceResult;

      addAddressEvent(ANALYTICS.ADDRESS_DECODED, {type: 'geocoding', googleResponse: placeDetails});

      const addressComponents = placeDetails?.address_components;

      if (addressComponents) {
        setSelectedLocation(extractGoogleAddressComponents(placeDetails, description));
      }
      setHasUserMovedMapManually(true);
      setInputValue(description);
      clearSuggestions();
    } catch (error) {
      console.error('Error fetching place details:', error);
    }
  };

  const {t} = useTranslation();

  function renderFullMapViewTitle() {
    return (
      <Box sx={styles.header}>
        <IconButton size="small" color="inherit" onClick={() => onSave(null)} aria-label="close" sx={styles.backButton}>
          <ChevronLeftIcon width={28} height={28} />
        </IconButton>
        <Typography sx={styles.title} fontWeight="bold" fontSize={20}>
          {t('addressV2.selectLocation')}
        </Typography>
        <Typography fontSize={16} color="#8E8E8E">
          1/2
        </Typography>
      </Box>
    );
  }

  //on press of select pin button
  const _onPinMapSelect = (selectedLocationFromGoogle: google.maps.GeocoderResult | undefined) => {
    const newSelectedLocation = selectedLocation || {coordinates: initialDefaultCoordinates};

    if (newSelectedLocation && newSelectedLocation.coordinates) {
      addAddressEvent(ANALYTICS.ADDRESS_LOCATION_CONFIRMED, {
        coordinates: {lat: newSelectedLocation?.coordinates.lat, lng: newSelectedLocation?.coordinates.lng}
      });
    }

    if (selectedLocationFromGoogle && config.IS_GOOGLE_ZIP_ENABLED) {
      const placeDetails = extractGoogleAddressComponents(selectedLocationFromGoogle, inputValue);

      onSave({
        ...placeDetails,
        subdistrictEn: placeDetails?.subdistrict,
        coordinates: newSelectedLocation.coordinates,
        zoomLevel: selectedLocation?.zoomLevel
      });
    } else {
      onSave(newSelectedLocation);
    }
  };

  const _onMapDrag = (lat: number, lng: number) => {
    //if coordinates are same as selected location, don't remove search value
    if (selectedLocation?.coordinates?.lat !== lat && inputValue) {
      setInputValue('');
    }

    setSelectedLocation({...selectedLocation, coordinates: {lat, lng}});
  };

  const _onZoomChange = (zoomLevel: number) => {
    setSelectedLocation({...selectedLocation, zoomLevel});
  };

  const renderMapContent = (status: Status) => {
    if (status === Status.FAILURE) return <Typography>Cannot load Google map</Typography>;
    if (status === Status.LOADING) return <Loading />;
    if (status === Status.SUCCESS && !isMapsApiLoaded) setIsMapsApiLoaded(true);

    return (
      <>
        <DialogTitle sx={{px: 2}}>
          {renderFullMapViewTitle()}
          {isMapsApiLoaded && ready ? (
            <SearchAddressInput searchValue={inputValue} setSearchValue={handleInputChange} />
          ) : null}
        </DialogTitle>
        <DialogContent sx={{padding: 0, justifyContent: 'center'}}>
          {searchValue && !!suggestions?.data?.length ? (
            <AddressSearchListView suggestions={suggestions} onSelect={onSuggestionSelect} />
          ) : (
            <PinMapView
              onPinSubmit={_onPinMapSelect}
              initialPinMapData={initialPinMapData}
              onLocationChange={_onMapDrag}
              onZoomChange={_onZoomChange}
              isAddressFormOpen={isAddressFormOpen}
              loading={!!loading}
              selectedLocation={selectedLocation}
              addressString={
                currentStore?.zipcode ? `${currentStore?.subdistrict}, ${currentStore?.zipcode}` : undefined
              }
              setHasUserMovedMapManually={setHasUserMovedMapManually}
            />
          )}
        </DialogContent>
      </>
    );
  };

  return (
    <StyledAddressFormDialog open={open} fullScreen={isMobile} onClose={() => onSave(null)}>
      <Wrapper apiKey={config.GOOGLE_MAP_API_KEY} libraries={['places', 'geometry']} render={renderMapContent} />
    </StyledAddressFormDialog>
  );
};

const styles = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    backgroundColor: theme.palette.common.mds_global_color_white
  },
  backButton: {
    // position: 'absolute',
    // left: 0
  },
  title: {
    fontWeight: 'bold',
    textAlign: 'center'
  }
};
